/**
 * Makes Home and Chat follow the app's own theme.
 *
 * The Default wallpaper has no photo, so the Home surface should simply be the
 * app: whatever Appearance has chosen (Desk, Slate, Fresh…), in light or in dark
 * mode. Otto's tokens (`--o-*`) are space-separated colour channels, while the
 * app's palette is ordinary CSS colours that the Appearance screen repaints on
 * the root element. This reads the palette back, turns each colour into
 * channels, and writes them onto the Otto tokens — and does it again whenever
 * the mode, the skin or the painted variables change.
 *
 * Photo themes opt out: their dark glass over a picture is their own look.
 */

/** Otto token ← the app colour it follows. */
const FOLLOW: Record<string, string> = {
  "--o-paper": "--ink-gray-9",
  "--o-muted": "--ink-gray-7",
  "--o-faint": "--ink-gray-5",
  "--o-surface": "--surface-white",
  "--o-raised": "--surface-gray-2",
  "--o-signal": "--ink-blue-3",
  "--o-signal-2": "--ink-blue-3",
};
const EDGE = "--outline-gray-2";
const PAGE = "--surface-gray-1";
const WHITE = "--surface-white";

type Channels = [number, number, number];

let probe: HTMLElement | null = null;
let observer: MutationObserver | null = null;
let last = "";
let queued = false;

function read(name: string): Channels | null {
  if (!probe) {
    probe = document.createElement("span");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = "position:fixed;left:-9999px;top:0;width:0;height:0;pointer-events:none";
    document.documentElement.appendChild(probe);
  }
  probe.style.color = "";
  probe.style.color = `var(${name})`;
  const m = getComputedStyle(probe).color.match(/[\d.]+/g);
  if (!m || m.length < 3) return null;
  return [Math.round(+m[0]), Math.round(+m[1]), Math.round(+m[2])];
}

const chan = (c: Channels): string => `${c[0]} ${c[1]} ${c[2]}`;
const alpha = (c: Channels, a: number): string => `rgb(${c[0]} ${c[1]} ${c[2]} / ${a})`;

function apply(): void {
  const root = document.documentElement;
  const got: Record<string, Channels> = {};
  for (const [, from] of Object.entries(FOLLOW)) {
    const c = read(from);
    if (c) got[from] = c;
  }
  const edge = read(EDGE);
  const grey = read(PAGE);
  const white = read(WHITE);
  if (!got["--ink-gray-9"] || !white || !edge || !grey) return;

  // The page must read as a different surface from the left rail, whatever the
  // skin: a grey rail gets a white page, and a white rail gets a grey one.
  const railEl = document.querySelector(".rail");
  const rail = railEl ? (getComputedStyle(railEl).backgroundColor.match(/[\d.]+/g) ?? []).slice(0, 3).map(Number) : [];
  const railIsWhite = rail.length === 3 && Math.abs(rail[0] - white[0]) + Math.abs(rail[1] - white[1]) + Math.abs(rail[2] - white[2]) < 12;
  const page: Channels = railIsWhite ? grey : white;

  // Nothing to do when neither the mode nor the skin has moved.
  const signature = JSON.stringify([got, edge, page, white, rail]);
  if (signature === last) return;
  last = signature;

  const s = root.style;
  for (const [token, from] of Object.entries(FOLLOW)) if (got[from]) s.setProperty(token, chan(got[from]));
  const ink = got["--ink-gray-9"];
  const muted = got["--ink-gray-7"] ?? ink;
  s.setProperty("--o-label-ink", chan(ink));
  s.setProperty("--o-label-ink-muted", chan(muted));
  s.setProperty("--o-on-photo", chan(ink));
  s.setProperty("--o-on-photo-muted", chan(muted));
  s.setProperty("--o-on-signal", chan(white));
  s.setProperty("--o-glass-tint", alpha(white, 0.96));
  s.setProperty("--o-hairline", alpha(edge, 1));
  s.setProperty("--o-highlight", alpha(white, 0.9));
  s.setProperty("--o-hover-bg", alpha(white, 1));
  s.setProperty("--o-chip-bg", alpha(white, 0.85));
  s.setProperty("--o-track-bg", alpha(edge, 1));
  s.setProperty("--o-disabled-bg", alpha(edge, 0.6));
  s.setProperty("--o-wallpaper", `linear-gradient(180deg, ${alpha(page, 1)} 0%, ${alpha(page, 1)} 100%)`);
}

function schedule(): void {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    apply();
  });
}

/** Follow the app's theme from now on, and paint it once now. */
export function followApp(): void {
  last = "";
  apply();
  if (observer) return;
  observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-skin", "data-chrome", "style"] });
}

/** Stop following and hand the tokens back to the stylesheet (photo themes). */
export function releaseApp(): void {
  observer?.disconnect();
  observer = null;
  last = "";
  const s = document.documentElement.style;
  for (const token of [
    ...Object.keys(FOLLOW),
    "--o-glass-tint", "--o-hairline", "--o-highlight", "--o-hover-bg", "--o-chip-bg", "--o-track-bg", "--o-disabled-bg",
  ]) s.removeProperty(token);
}
